/**
 * The root layout's redirect cascade, ported (RO-0-05; source of truth for the
 * behaviour is `excellent/app/layout.tsx` lines 161–344, which is NOT modified —
 * both stacks run side by side and the Next tree is the rollback, PRD D6).
 *
 * ── Why this ports cleanly ──
 *
 * Under Next this ran as request-time SERVER logic: `proxy.ts` stamps
 * `x-pathname` / `x-search` on every request, the root layout reads them, and
 * each gate calls `next/navigation`'s throw-based `redirect()`. Everything it
 * actually DECIDES with, though, is a string: `resolveModeRedirect`,
 * `modeRedirectUrl` and `isPublicHiringPath` (`excellent/src/modes/redirects.ts`)
 * are a pure resolver with no `server-only`, no DB and no `next/*`. So the
 * cascade moves to the client verbatim — the only change is throw-based control
 * flow becoming a returned destination, which TanStack's `beforeLoad` throws as
 * a typed `redirect({ to })`.
 *
 * ORDER IS BEHAVIOUR. The gates are sequential and first-match-wins in the
 * original; a reordering silently changes which destination a path reaches (e.g.
 * the `/lens` fold deliberately sits ABOVE the modes pass so an old `/lens`
 * bookmark lands on Analytics rather than being folded as a mode). They are
 * numbered below in the source's own order — keep it.
 *
 * TWO GATES ARE NOT PURE and are therefore parameters, not logic: the onboarding
 * gate reads `isOnboardingActive()` and the needs-workspace gate reads
 * `resolveTenant().needsWorkspace`, both direct SQLite reads. They arrive as
 * `ShellGates` (see `shell-data.ts`) so this module stays testable and
 * bundle-safe (PRD §5 I4).
 */

import {
  resolveModeRedirect,
  modeRedirectUrl,
  isPublicHiringPath,
} from "@/modes/redirects";
import { isDevelopmentOnlyProductPath } from "../prototypes/bare-canvas";

/** The two server-resolved booleans the cascade cannot decide on its own. */
export interface ShellGates {
  /** `isOnboardingActive()` — bounce to the `/welcome` hub until setup completes. */
  onboardingActive: boolean;
  /** `resolveTenant().needsWorkspace` — an authed user with no workspace membership. */
  needsWorkspace: boolean;
}

/**
 * Chrome-free takeovers: these render `children` straight into `<body>` with no
 * rail, nav, dock or peek host. Mirrors the layout's `isWelcome || isLogin ||
 * isTransparency || isConnected || isWorkspaceOnboarding` ternary.
 */
function queryFlagEnabled(search: string, key: string): boolean {
  const raw = search.startsWith("?") ? search.slice(1) : search;
  const value = new URLSearchParams(raw).get(key);
  return value === "1" || value === "true";
}

function isPublicEntryPath(pathname: string, search: string = ""): boolean {
  if (pathname === "/login" || pathname === "/join") return true;
  if (!isPublicHiringPath(pathname)) return false;
  if (pathname.startsWith("/hiring/api/")) return true;
  const forcePublic = queryFlagEnabled(search, "public");
  const forceInternal = queryFlagEnabled(search, "internal");
  return forcePublic || !forceInternal;
}

export function isTakeoverPath(pathname: string, search: string = ""): boolean {
  return (
    pathname.startsWith("/welcome") ||
    isPublicEntryPath(pathname, search) ||
    pathname === "/transparency" ||
    pathname === "/integrations/connected" ||
    pathname.startsWith("/onboarding/workspace")
  );
}

/**
 * Resolve the destination for a path, or `null` to render it where it is.
 *
 * @param pathname location.pathname
 * @param search   location.search, INCLUDING the leading `?` (this is what
 *                 `x-search` carried, and the `area=favorites` test below and
 *                 `resolveModeRedirect` both depend on that shape).
 */
export function resolveShellRedirect(
  pathname: string,
  search: string,
  gates: ShellGates,
): string | null {
  const isWelcome = pathname.startsWith("/welcome");
  // `/api` is the Hono host's namespace; it never reaches the SPA router in
  // production, but the guard is kept so the cascade stays a faithful port.
  const isApiRoute = pathname.startsWith("/api");
  const isTransparency = pathname === "/transparency";
  const isConnected = pathname === "/integrations/connected";
  const isWorkspaceOnboarding = pathname.startsWith("/onboarding/workspace");
  const isPublicEntry = isPublicEntryPath(pathname, search);

  // 1 — Needs-workspace gate. Account scope must exist before onboarding can
  // mutate tenant state. Anonymous local mode already resolves to workspace 1.
  if (
    !isWelcome &&
    !isTransparency &&
    !isConnected &&
    !isApiRoute &&
    !isDevelopmentOnlyProductPath(pathname) &&
    !isWorkspaceOnboarding &&
    !isPublicEntry &&
    gates.needsWorkspace
  ) {
    return "/onboarding/workspace";
  }

  // 2 — Genuine first run. Until the onboarding record is complete, the
  // chrome-free Welcome surface owns local agent recognition and the explicit
  // Configure action. Public/takeover/development surfaces remain reachable.
  if (
    gates.onboardingActive &&
    !isWelcome &&
    !isTransparency &&
    !isConnected &&
    !isApiRoute &&
    !isDevelopmentOnlyProductPath(pathname) &&
    !isWorkspaceOnboarding &&
    !isPublicEntry
  ) {
    return "/welcome";
  }

  // Activity is a canonical companion surface. Keep it in place after account
  // and workspace gates have run instead of letting the legacy Work-mode
  // resolver fold it back to `/work`.
  if (pathname === "/activity") return null;

  // 3 — Lens fold. Next sends these to `/agents/analytics/you/*` (the Claude
  // Code observability surface). That page is unmounted here (#44575 owns the
  // analytics port), so leftover dests land on product home `/`.
  // NOT `/lens/api/*`, which is the live data layer. Above the modes
  // pass on purpose.
  if (!isWelcome && (pathname === "/lens" || pathname.startsWith("/lens/")) && !pathname.startsWith("/lens/api")) {
    return "/";
  }

  // 4 — Documentation was extracted into the standalone selfdocs app (#43475);
  // forward retired doc bookmarks to the verifier product home.
  if (!isWelcome && (pathname === "/document" || pathname.startsWith("/document/"))) {
    return "/";
  }
  if (!isWelcome && /^\/projects\/[^/]+\/development\/docs(\/.*)?$/.test(pathname)) {
    return "/";
  }

  // 5 — Retired Agents sub-surfaces (Roles registry, Context Inspector). The
  // modes pass below deliberately SKIPS `/agents/*`, so without this explicit
  // fold an old bookmark would 404. NOT `/agents/api/*` (the context-pack data
  // layer), and NOT `/agents/roadmap` or `/agents/goals/*` (still live).
  if (
    !isWelcome &&
    (pathname === "/agents/roles" ||
      pathname.startsWith("/agents/roles/") ||
      pathname === "/agents/context" ||
      pathname.startsWith("/agents/context/"))
  ) {
    return "/";
  }

  // 6 — Integrations fold. The standalone `/integrations/*` UI tree is a dead
  // duplicate of `/settings/integrations/*`. NOT `/integrations/api/*` (the live
  // OAuth + sync data layer), and not the connection-result takeover.
  if (
    !isWelcome &&
    (pathname === "/integrations" || pathname.startsWith("/integrations/")) &&
    !pathname.startsWith("/integrations/api") &&
    !isConnected
  ) {
    return `/settings${pathname}`;
  }

  // 7 — Favorites is a first-class standalone page, not a Work Domain. Forward
  // both legacy forms. `search` carries its leading `?`, so the test matches
  // `?area=favorites` and `&area=favorites` alike.
  if (
    !isWelcome &&
    (pathname === "/work/favorites" ||
      (pathname === "/work" && /(^|[?&])area=favorites(&|$)/.test(search)))
  ) {
    return "/favorites";
  }

  // 8 — Modes nav. The single point that forwards EVERY legacy surface to its
  // mode; deep paths are preserved as `?sub=`. Skipped for `/agents/*` (owns
  // its routes), `/personal/api` (the Personal data layer; the UI root folds
  // into Work), and the public careers page. `dest !== pathname + search` guards
  // the self-mapping routes against a loop.
  if (
    !isWelcome &&
    !isApiRoute &&
    !pathname.startsWith("/agents") &&
    !pathname.startsWith("/personal/api") &&
    !isPublicHiringPath(pathname)
  ) {
    // Resolved against the FULL path+query: the resolver reads `?view=review` to
    // route the inbox surfaces to the canonical review door (/agents/verification).
    const r = resolveModeRedirect(pathname + search);
    if (r) {
      const dest = modeRedirectUrl(r, search ? new URLSearchParams(search) : undefined);
      if (dest !== pathname + search) return dest;
    }
  }

  return null;
}
